// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './AgentPanel.module.css';
import { AgentPersona, PERSONAS } from './agent-personas';
import { Conversation, Turn, AgentTurnState, ProposalSummary } from './agent-conversation';

// The Agents-tab panel (Activities zone) -- the imperative shell over the pure
// agent-conversation core. Presentation-only, like ActivityRail: it renders a
// Conversation and emits intent (send / cancel / accept / reject / select
// persona); the Editor owns the conversation state and the sd-ai + canvas wiring.
// Anti-Stella (A2): the surface is personas + a chat thread -- no URL, mode,
// assistant, or reasoning controls. All user-facing copy here is a build-time
// PLACEHOLDER; the finished ST4AI-voice copy is Coherence-supplied before the
// acceptance re-pass.

export interface AgentPanelProps {
  /** The shared thread to render. */
  conversation: Conversation;
  /** Personas to show in the strip -- already tier-filtered (availablePersonas). */
  personas: readonly AgentPersona[];
  /** The persona a new message is addressed to. */
  activePersona: AgentPersona;
  onSelectPersona: (persona: AgentPersona) => void;
  onSend: (text: string) => void;
  onCancel: (turnId: string) => void;
  onDecide: (turnId: string, decision: 'accepted' | 'rejected') => void;
  /** True while a turn is working -- send is disabled, the working turn shows Stop. */
  busy: boolean;
  /** Input placeholder (Coherence copy later). */
  inputPlaceholder?: string;
}

const DEFAULT_PLACEHOLDER = 'Ask Merlin to build or change the model…';

function DiffChips({ label, names, kind }: { label: string; names: readonly string[]; kind: string }): React.ReactElement | null {
  if (names.length === 0) {
    return null;
  }
  return (
    <div className={styles.diffRow}>
      <span className={styles.diffLabel}>{label}</span>
      {names.map((n) => (
        <span key={n} className={`${styles.chip} ${styles[kind]}`}>
          {n}
        </span>
      ))}
    </div>
  );
}

function ProposalCard({
  turnId,
  proposal,
  decision,
  onDecide,
}: {
  turnId: string;
  proposal: ProposalSummary;
  decision: 'pending' | 'accepted' | 'rejected';
  onDecide: (turnId: string, decision: 'accepted' | 'rejected') => void;
}): React.ReactElement {
  return (
    <div className={styles.proposal}>
      <div className={styles.proposalSummary}>{proposal.summary}</div>
      <DiffChips label="Add" names={proposal.added} kind="added" />
      <DiffChips label="Update" names={proposal.changed} kind="changed" />
      <DiffChips label="Remove" names={proposal.removed} kind="removed" />
      {decision === 'pending' ? (
        <div className={styles.actions}>
          <button type="button" className={`${styles.btn} ${styles.accept}`} onClick={() => onDecide(turnId, 'accepted')}>
            Accept
          </button>
          <button type="button" className={`${styles.btn} ${styles.reject}`} onClick={() => onDecide(turnId, 'rejected')}>
            Reject
          </button>
        </div>
      ) : (
        <div className={`${styles.decided} ${decision === 'accepted' ? styles.acceptedText : styles.rejectedText}`}>
          {decision === 'accepted' ? 'Accepted' : 'Rejected'}
        </div>
      )}
    </div>
  );
}

function AgentTurnBody({
  turnId,
  state,
  onCancel,
  onDecide,
}: {
  turnId: string;
  state: AgentTurnState;
  onCancel: (turnId: string) => void;
  onDecide: (turnId: string, decision: 'accepted' | 'rejected') => void;
}): React.ReactElement {
  switch (state.status) {
    case 'working':
      return (
        <div className={styles.working}>
          <span className={styles.dots} aria-hidden="true" />
          <span className={styles.workingText}>Working…</span>
          <button type="button" className={`${styles.btn} ${styles.stop}`} onClick={() => onCancel(turnId)}>
            Stop
          </button>
        </div>
      );
    case 'message':
      return <div className={styles.message}>{state.text}</div>;
    case 'proposal':
      return <ProposalCard turnId={turnId} proposal={state.proposal} decision={state.decision} onDecide={onDecide} />;
    case 'error':
      return <div className={styles.error}>{state.reason}</div>;
    case 'cancelled':
      return <div className={styles.cancelled}>Stopped.</div>;
  }
}

function TurnRow({
  turn,
  onCancel,
  onDecide,
}: {
  turn: Turn;
  onCancel: (turnId: string) => void;
  onDecide: (turnId: string, decision: 'accepted' | 'rejected') => void;
}): React.ReactElement {
  if (turn.role === 'user') {
    return (
      <div className={`${styles.turn} ${styles.userTurn}`}>
        <div className={styles.userBubble}>{turn.text}</div>
      </div>
    );
  }
  const meta = PERSONAS[turn.persona];
  return (
    <div className={`${styles.turn} ${styles.agentTurn}`}>
      <div className={styles.persona} aria-hidden="true">
        {meta.mark}
      </div>
      <div className={styles.agentBody}>
        <div className={styles.personaName}>{meta.name}</div>
        <AgentTurnBody turnId={turn.id} state={turn.state} onCancel={onCancel} onDecide={onDecide} />
      </div>
    </div>
  );
}

/**
 * The Agents-tab panel. A function component with only local input-draft state;
 * all conversation state lives in the Editor and flows in via `conversation`.
 */
export function AgentPanel(props: AgentPanelProps): React.ReactElement {
  const { conversation, personas, activePersona, onSelectPersona, onSend, onCancel, onDecide, busy } = props;
  const [draft, setDraft] = React.useState('');

  const submit = (): void => {
    const text = draft.trim();
    if (!text || busy) {
      return;
    }
    onSend(text);
    setDraft('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends; Shift+Enter is a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.strip} role="group" aria-label="Agents">
        {personas.map((p) => {
          const meta = PERSONAS[p];
          const active = p === activePersona;
          return (
            <button
              key={p}
              type="button"
              aria-pressed={active}
              className={active ? `${styles.chipPersona} ${styles.chipPersonaActive}` : styles.chipPersona}
              onClick={() => onSelectPersona(p)}
            >
              <span className={styles.chipMark} aria-hidden="true">
                {meta.mark}
              </span>
              <span className={styles.chipName}>{meta.name}</span>
              <span className={styles.chipRole}>{meta.role}</span>
            </button>
          );
        })}
      </div>

      <div className={styles.thread} role="log" aria-live="polite" aria-label="Conversation">
        {conversation.turns.map((turn) => (
          <TurnRow key={turn.id} turn={turn} onCancel={onCancel} onDecide={onDecide} />
        ))}
      </div>

      <div className={styles.composer}>
        <textarea
          className={styles.input}
          value={draft}
          placeholder={props.inputPlaceholder ?? DEFAULT_PLACEHOLDER}
          aria-label="Message"
          rows={2}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className={`${styles.btn} ${styles.send}`}
          onClick={submit}
          disabled={busy || draft.trim().length === 0}
        >
          Send
        </button>
      </div>
    </div>
  );
}
